import React, { Fragment, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useParams, useNavigate, useLocation } from 'react-router-dom'
import { Button, Container, Spinner, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
import { CustomAlert } from '../../components/CustomAlert/CustomAlert'
import { CustomTooltip } from '../../components/CustomTooltip/CustomTooltip'
import { Questionary } from '../../components/Exam/Questionary'
import { getTenantByKey } from '../../helpers/helpers'
import { useExam } from '../../hooks/useExam'

export const ExamQuestionary = () => {

    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const { id, employeeId, visitId } = useParams();

    const {
        exam,
        examState,
        tenant,
        questionaryAnswers,
        videoPercentage,
        wasSeen,
        setWasSeen,
        setCorrect,
        isDisabled,
        sendExam,
        sendEmptyExam,
        resetQuestionary,
        verify,
        sending,
        feedbackComment,
        setFeedbackComment,
    } = useExam(id, employeeId, visitId);

    const [showCancelModal, setShowCancelModal] = React.useState(false);

    // Si viene del botón "Iniciar Examen", establecer wasSeen como true
    useEffect(() => {
        if (location.state?.fromStartButton && location.state?.wasSeen) {
            setWasSeen(true);
        }
    }, [location.state, setWasSeen]);

    // Redirigir al material si no está completado (solo si no viene del botón)
    useEffect(() => {
        // No redirigir si viene del botón "Iniciar Examen"
        if (location.state?.fromStartButton) {
            return;
        }
        
        if (verify && exam && !wasSeen) {
            const materialPath = employeeId 
                ? `/exam/material/${id}/employee/${employeeId}`
                : `/exam/material/${id}/visit/${visitId}`;
            navigate(materialPath, { replace: true });
        }
    }, [wasSeen, exam, verify, id, employeeId, visitId, navigate, location.state]);

    const handleCancel = () => {
        setShowCancelModal(true);
    };

    const confirmCancel = async () => {
        // Cerrar el modal primero
        setShowCancelModal(false);
        
        // Enviar el examen vacío para que se cuente el intento
        if (exam && exam.questionary) {
            try {
                await sendEmptyExam();
            } catch (error) {
                console.error('Error al enviar examen vacío:', error);
            }
        }
        
        // Redirigir al material después de enviar (esto sobrescribirá la redirección automática al resultado)
        const materialPath = employeeId 
            ? `/exam/material/${id}/employee/${employeeId}`
            : `/exam/material/${id}/visit/${visitId}`;
        navigate(materialPath, { replace: true });
    };

    const getTooltipText = () => {
        if (!exam || !exam.induction || !exam.induction.files) return '';
        const hasPdf = exam.induction.files.some((f) => f.type === 'application/pdf');
        const hasVideo = exam.induction.files.some((f) => f.type === 'video/mp4');
        if (hasPdf && hasVideo) {
            return `${t('questionary.exam_disabled_pdf_and_video_part_one')} ${videoPercentage}% ${t('questionary.exam_disabled_video_part_two')}`;
        }
        if (hasPdf) {
            return t('questionary.exam_disabled_pdf');
        }
        if (hasVideo) {
            return `${t('questionary.exam_disabled_video_part_one')} ${videoPercentage}% ${t('questionary.exam_disabled_video_part_two')}`;
        }
        return '';
    }

    if (tenant.error) {
        return <Navigate to={'/login'} replace />
    }

    if (tenant.isFetching || examState.isFetching || sending) {
        return (
            <div className='position-absolute top-50 start-50'>
                <Spinner size={'xl'} />
            </div>
        )
    }

    if (!verify) {
        return <Navigate to={employeeId ? `/exam/material/${id}/employee/${employeeId}` : `/exam/material/${id}/visit/${visitId}`} replace />
    }

    if (verify && exam && (exam.viewed === true && exam.approved === true)) {
        return <Navigate to={employeeId ? `/exam/result/${id}/employee/${employeeId}` : `/exam/result/${id}/visit/${visitId}`} replace />
    }

    if (verify && exam && !exam.can_take_exam) {
        return (
            <Container className="bg-white pb-5 pt-5 mt-5 rounded-3" fluid="md">
                <div className="text-center">
                    <img src={getTenantByKey('logo')} alt="" className="mb-4" />
                    <CustomAlert
                        color="danger"
                        text={exam.induction.failed_attempts_message || t('exam.max_attempts_reached')}
                    />
                </div>
            </Container>
        )
    }

    if (!exam || !exam.questionary) {
        return <Navigate to={employeeId ? `/exam/material/${id}/employee/${employeeId}` : `/exam/material/${id}/visit/${visitId}`} replace />
    }

    return (
        <Fragment>
            <Container className="bg-white pb-5 pt-5 mt-5 rounded-3" fluid="md">
                {
                    exam ?
                        <>
                            <div className='row'>
                                <div className='d-flex aling-items-center justify-content-between flex-wrap mb-3'>
                                    {
                                        employeeId ?
                                            <h4>{exam.contractor_name} <small>({exam.contractor_code})</small></h4>
                                            :
                                            <h4>{exam.visit.first_name}, {exam.visit.last_name}</h4>
                                    }
                                    <img src={getTenantByKey('logo')} alt="" />
                                </div>
                                <div className='col-lg-12'>
                                    <Questionary
                                        questionary={exam.questionary}
                                        setCorrect={setCorrect}
                                        questionaryAnswers={questionaryAnswers}
                                        feedbackComment={feedbackComment}
                                        setFeedbackComment={setFeedbackComment}
                                        finishButton={
                                            <>
                                                {
                                                    isDisabled() ?
                                                        <CustomTooltip
                                                            placement={"top"}
                                                            target={'send'}
                                                            text={`${getTooltipText()}${t('questionary.exam_disabled_questionary')}`}
                                                        />
                                                        :
                                                        null
                                                }
                                                <div id='send'>
                                                    <Button onClick={sendExam} color='primary'>{t('common.end')}</Button>
                                                </div>
                                            </>
                                        }
                                        cancelButton={
                                            <Button onClick={handleCancel}>
                                                {t('common.cancel')}
                                            </Button>
                                        }
                                    />
                                </div>
                            </div>
                        </>
                        :
                        null
                }
                {examState.error ? <CustomAlert color={'danger'} text={examState.message} /> : null}
            </Container>

            {/* Modal de confirmación al cancelar */}
            <Modal isOpen={showCancelModal} toggle={() => setShowCancelModal(false)}>
                <ModalHeader toggle={() => setShowCancelModal(false)}>
                    {t('questionary.cancel_questionary_title')}
                </ModalHeader>
                <ModalBody>
                    {t('questionary.cancel_questionary_message')}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setShowCancelModal(false)}>
                        {t('common.cancel')}
                    </Button>
                    <Button color="danger" onClick={confirmCancel}>
                        {t('questionary.confirm_cancel')}
                    </Button>
                </ModalFooter>
            </Modal>
        </Fragment>
    )
}

